import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ChartLine, PencilSimple} from '../../icons';
import {Spinner} from '../../Spinner';
import {ClientEconomicsApi} from '../../../api/clientEconomicsApi';
import {daysSince, formatCompactMoney, formatFullMoney, formatGoalValue, relativeTimeLabel} from './panelFormatters';
import {
    PROFILE_FIELDS,
    SOURCE_LABEL_KEYS,
    economyReasonText,
    formatEconomyDate,
    formatPct,
    formatProfileValue,
    formatRoas,
    formatSignedMoney,
    lineCurrencies,
    lineDerivedReason,
    reasonFor,
} from './economyFormatters';
import './EconomyCard.scss';

// Umbral del aviso «el ticket real se alejó del cargado» (contrato §6, `TICKET_DRIFT`): el mismo
// número que dispara la alerta semanal, así la card y el correo avisan juntos.
const TICKET_DRIFT_THRESHOLD_PCT = 15;

// Qué dice el ROAS marginal contra el equilibrio (§2, `spend_cap.verdict`).
const VERDICTS = new Set(['ABOVE', 'AT', 'BELOW']);

// Los avisos de la derivación de Escalafy (`escalafy/profile_deriver.py`, `WARNING_*`): el backend
// manda el código y el copy lo arma el FE. Un código que no está acá no se muestra.
const ESCALAFY_WARNINGS = new Set([
    'NO_SALES', 'FEW_DAYS_WITH_SALES', 'COGS_HIDDEN', 'NO_PRODUCT_COSTS', 'GROSS_MARGIN_OUT_OF_RANGE',
    'NO_ORDERS', 'SHIPPING_NOT_AVAILABLE', 'NET_REVENUE_HIDDEN', 'NEGATIVE_FEES_CLAMPED',
    'PAYMENT_FEE_OUT_OF_RANGE', 'COSTS_NOT_AVAILABLE', 'NO_FIXED_COSTS', 'COSTS_IN_OTHER_CURRENCY',
    'VARIABLE_COSTS_NOT_INCLUDED', 'AGENCY_FEE_GUESSED', 'AGENCY_FEE_NOT_FOUND', 'INCLUDES_MERCADOLIBRE',
]);

// Los errores de «Usar los datos de Escalafy» que tienen salida conocida (R-ecofe-10).
const ESCALAFY_USE_ERROR_KEYS = {
    MANUAL_VERSION_TODAY: 'economics.escalafy.error.manual_version_today',
    NO_ESCALAFY_PROPOSAL: 'economics.escalafy.error.no_proposal',
};

// Qué texto lleva el chip de Escalafy según el estado de la conexión (§3.1). Un estado que no está
// acá (ORG_NOT_FOUND, uno futuro) se lee como desconectado: la salida es la misma, reconectar.
const ESCALAFY_STATUS_KEYS = {
    DISCONNECTED: 'client_panel.economy.escalafy.disconnected',
    LIMITED_ROLE: 'client_panel.economy.escalafy.limited_role',
    PLAN_WITHOUT_API: 'client_panel.economy.escalafy.plan_without_api',
};

/**
 * Un derivado. Si el backend no lo pudo calcular, el tile NO se esconde: dice por qué, con el texto
 * de ese motivo (sin tienda en la línea, pocos días de histórico, moneda distinta…). Un tile que
 * desaparece se lee como «esto no existe», y el punto es que el usuario sepa qué le falta.
 */
function EconomyTile({label, value, hint, reasonText, reasonLink, tone, testId}) {
    const showValue = value !== null && value !== undefined;
    return (
        <div className={`eco-tile${tone ? ` -${tone}` : ''}`} data-testid={testId}>
            <span className="eco-tile__label">{label}</span>
            <span className={`eco-tile__value${showValue ? '' : ' -na'}`}>{showValue ? value : '—'}</span>
            {reasonText ? (
                <span className="eco-tile__reason">
                    {reasonLink ? <Link to={reasonLink}>{reasonText}</Link> : reasonText}
                </span>
            ) : (hint && <span className="eco-tile__hint">{hint}</span>)}
        </div>
    );
}

/**
 * Card «Economía del negocio» de la ficha 2.1 (bloque `economy` del agregado; tanda Economía del
 * cliente, mockup 1).
 *
 * Como todas las cards del Panel, NO hace fetch: recibe su bloque. La carga y la edición viven en
 * su propia pantalla (`/client/:id/economia`) — la ficha ya no abre paneles laterales (card
 * bBcBPqGg), así que «Editar» navega.
 *
 * Tres estados, además del de carga: sin módulo el shell no la monta (`enabled:false`); sin perfil,
 * el vacío con las dos vías (cargar o conectar Escalafy); con perfil, lo cargado a la izquierda y los
 * cuatro derivados a la derecha. Si las líneas están en monedas distintas no hay total del cliente
 * (`client_totals: null`): la card lista cada línea con sus números en vez de sumar monedas.
 */
export function EconomyCard({data, loading, clientId, clientName, onRefetch}) {
    const {t, i18n} = useTranslation();
    const language = i18n && i18n.language;
    const [usingEscalafy, setUsingEscalafy] = useState(false);

    if (loading) {
        return (
            <div className="box pnl-card eco-card">
                <div className="box-loader"><Spinner color="positive small"/></div>
            </div>
        );
    }
    if (!data || data.enabled === false) return null;

    const economiaPath = `/client/${clientId}/economia`;
    const configured = data.configured === true;
    const profile = data.profile || {};
    const currency = data.currency || profile.currency || null;
    const client = clientName || '';

    const renderHead = () => (
        <div className="pnl-card__head">
            <h2 className="pnl-card__title">{t('client_panel.economy.title')}</h2>
            {configured && data.stale === true && (
                <span className="eco-badge -review">{t('client_panel.economy.stale.badge')}</span>
            )}
            <span className="pnl-card__spacer"/>
            {configured && (
                <Link className="pnl-card__link eco-card__edit" to={economiaPath}>
                    <PencilSimple size={13} weight="bold"/>
                    {t('client_panel.economy.edit')}
                </Link>
            )}
        </div>
    );

    // Con Escalafy ya vinculado (entre el vínculo y el primer sync, o para siempre si el sync no deriva
    // nada), el vacío dice en qué estado está la conexión en vez de invitar a conectar otra vez: cada
    // OAuth nuevo deja una credencial más (R-ecofe-08).
    const renderEmpty = () => {
        const escalafyLinked = !!(data.escalafy && data.escalafy.connected === true);
        return (
            <div className="eco-empty">
                <span className="eco-empty__icon" aria-hidden="true"><ChartLine size={22} weight="bold"/></span>
                <p className="eco-empty__txt">
                    <strong>{t('client_panel.economy.empty.title', {client, interpolation: {escapeValue: false}})}</strong>
                    {' '}
                    {t('client_panel.economy.empty.body')}
                </p>
                <div className="eco-empty__acts">
                    {escalafyLinked ? renderEscalafyChip() : (
                        <Link className="pnl-btn pnl-btn--ghost pnl-btn--sm" to={`${economiaPath}#escalafy`}>
                            {t('client_panel.economy.empty.cta_escalafy')}
                        </Link>
                    )}
                    <Link className="pnl-btn pnl-btn--primary pnl-btn--sm" to={economiaPath}>
                        {t('client_panel.economy.empty.cta')}
                    </Link>
                </div>
            </div>
        );
    };

    // El ticket es el único dato que puede no haberse cargado a propósito: sin valor, Dashbo lo
    // calcula de las órdenes pagadas de la tienda. Se dice de dónde sale, no se muestra un vacío.
    const renderTicketValue = () => {
        if (profile.avg_ticket !== null && profile.avg_ticket !== undefined) {
            return <strong>{formatFullMoney(currency, profile.avg_ticket) || '—'}</strong>;
        }
        const computed = data.avg_ticket_computed;
        if (computed && computed.value !== null && computed.value !== undefined) {
            return (
                <span className="eco-in__computed">
                    <strong>{formatFullMoney(currency, computed.value)}</strong>
                    <span className="eco-in__sub">
                        {t('client_panel.economy.input.avg_ticket_computed', {
                            orders: computed.orders_90d || 0, count: Number(computed.orders_90d) || 0,
                        })}
                    </span>
                </span>
            );
        }
        return <strong className="eco-in__na">—</strong>;
    };

    const renderInputs = () => (
        <div className="eco-in">
            <div className="eco-in__head">{t('client_panel.economy.inputs_head')}</div>
            {PROFILE_FIELDS.map((field) => (
                <div className="eco-in__row" key={field}>
                    <span className="eco-in__label">{t(`client_panel.economy.input.${field}`)}</span>
                    {field === 'avg_ticket' ? renderTicketValue() : (
                        <strong>{formatProfileValue(field, profile[field], currency) || '—'}</strong>
                    )}
                </div>
            ))}
        </div>
    );

    // Los tiles son los totales del cliente: un `CURRENCY_MISMATCH` ahí es «las líneas con tienda
    // quedaron afuera por la moneda» (QA-ecob-N02), y cuál lo dice la nota de las líneas excluidas.
    const reasonText = (block, field) => economyReasonText(reasonFor(block, field), t, {
        profileCurrency: currency, totals: true,
    });
    // «Conecta la tienda…» lleva a donde se conecta: es la única razón que se resuelve con un clic.
    const reasonLinkFor = (block, field) => (reasonFor(block, field) === 'NO_STORE'
        ? `/client/${clientId}/conections` : null);

    // Las líneas SIN tienda (marca, awareness): su inversión se RESTA de la ganancia del cliente
    // (decisión de fede, C-econ-be-06): la venta es la de toda la tienda e incluye lo que esas
    // campañas ayudan a vender. El tope, en cambio, mira sólo las líneas con tienda.
    const linesWithoutStore = (totals) => (Array.isArray(totals.lines_without_store)
        ? totals.lines_without_store.filter((line) => line && line.name) : []);

    const profitHint = (totals, elapsedDays) => {
        const noStoreSpend = Number(totals.spend_without_store);
        const params = {
            spend: formatCompactMoney(currency, totals.spend) || '—',
            revenue: formatCompactMoney(currency, totals.store_revenue) || '—',
            days: elapsedDays,
            count: elapsedDays,
            interpolation: {escapeValue: false},
        };
        if (!Number.isFinite(noStoreSpend) || noStoreSpend <= 0) {
            return t('client_panel.economy.derived.estimated_profit_hint', params);
        }
        return t('client_panel.economy.derived.estimated_profit_hint_with_no_store', {
            ...params, no_store: formatCompactMoney(currency, noStoreSpend),
        });
    };

    const renderTiles = (totals) => {
        const period = totals.period || {};
        const profit = Number(totals.estimated_profit);
        const historyDays = totals.spend_series_days !== undefined
            ? totals.spend_series_days : (totals.inputs || {}).spend_series_days;
        const historyHint = historyDays !== undefined && historyDays !== null
            ? t('client_panel.economy.derived.spend_cap_hint', {days: historyDays, count: Number(historyDays)})
            : null;
        const capHint = historyHint && linesWithoutStore(totals).length
            ? `${historyHint} · ${t('client_panel.economy.derived.spend_cap_only_with_store')}` : historyHint;
        const elapsedDays = period.elapsed_days || 0;
        const verdict = VERDICTS.has(totals.verdict) && totals.spend_cap !== null && totals.spend_cap !== undefined
            ? t(`client_panel.economy.verdict.${totals.verdict}`) : null;

        return (
            <div className="eco-out">
                <EconomyTile
                    testId="eco-tile-contribution"
                    label={t('client_panel.economy.derived.contribution_margin')}
                    value={formatPct(totals.contribution_margin_pct)}
                    hint={t('client_panel.economy.derived.contribution_margin_hint')}
                    reasonText={reasonText(totals, 'contribution_margin_pct')}
                />
                <EconomyTile
                    testId="eco-tile-breakeven"
                    label={t('client_panel.economy.derived.breakeven_roas')}
                    value={formatRoas(totals.breakeven_roas)}
                    hint={t('client_panel.economy.derived.breakeven_roas_hint')}
                    reasonText={reasonText(totals, 'breakeven_roas')}
                />
                <EconomyTile
                    testId="eco-tile-profit"
                    label={t('client_panel.economy.derived.estimated_profit')}
                    value={formatSignedMoney(currency, totals.estimated_profit)}
                    tone={Number.isFinite(profit) && totals.estimated_profit !== null
                        ? (profit >= 0 ? 'pos' : 'neg') : null}
                    hint={profitHint(totals, elapsedDays)}
                    reasonText={reasonText(totals, 'estimated_profit')}
                    reasonLink={reasonLinkFor(totals, 'estimated_profit')}
                />
                <EconomyTile
                    testId="eco-tile-cap"
                    label={t('client_panel.economy.derived.spend_cap')}
                    value={formatCompactMoney(currency, totals.spend_cap)}
                    hint={[capHint, verdict].filter(Boolean).join(' ') || null}
                    reasonText={reasonText(totals, 'spend_cap')}
                    reasonLink={reasonLinkFor(totals, 'spend_cap')}
                />
            </div>
        );
    };

    // Los totales del cliente suman SÓLO las líneas con derivados: una línea en otra moneda (o con una
    // cuenta en otra moneda) queda afuera sin que los tiles lo digan. Se nombra debajo (R-ecofe-17).
    // Las líneas que quedaron AFUERA de los totales, con su motivo. La lista la manda el backend
    // (`client_totals.excluded_lines`); sin ella (backend viejo) se arma con las líneas sin `derived`.
    // Las monedas del desajuste salen de la línea del bloque (una CUENTA puede ser la de otra moneda).
    const excludedLines = (totals) => {
        const lines = data.lines || [];
        if (totals && Array.isArray(totals.excluded_lines)) {
            return totals.excluded_lines.map((excluded) => ({
                ...(lines.find((line) => line.business_unit_id === excluded.business_unit_id) || {}),
                ...excluded,
            }));
        }
        return lines.filter((line) => !line.derived && lineDerivedReason(line))
            .map((line) => ({...line, reason: lineDerivedReason(line)}));
    };

    const renderExcludedLines = (totals) => {
        const excluded = excludedLines(totals)
            .map((line) => ({line, text: economyReasonText(line.reason, t, lineCurrencies(line, currency))}))
            .filter(({text}) => !!text);
        if (!excluded.length) return null;
        return (
            <ul className="eco-excluded" data-testid="eco-excluded-lines">
                {excluded.map(({line, text}) => (
                    <li className="eco-note -warn" key={line.business_unit_id}>
                        <strong>{line.name}</strong>
                        {': '}
                        {text}
                    </li>
                ))}
            </ul>
        );
    };

    // Nota informativa (no un aviso): qué líneas sin tienda descuenta la ganancia. Reemplaza la nota de
    // «excluidas» que esas líneas tenían antes de C-econ-be-06, cuando no entraban en la cuenta.
    const renderLinesWithoutStore = (totals) => {
        const lines = linesWithoutStore(totals);
        if (!lines.length) return null;
        return (
            <p className="eco-note" data-testid="eco-lines-without-store">
                {t('client_panel.economy.note.lines_without_store', {
                    names: lines.map((line) => line.name).join(', '),
                    count: lines.length,
                    interpolation: {escapeValue: false},
                })}
            </p>
        );
    };

    // Sin total del cliente: por monedas distintas (no hay un total honesto — convertir con la
    // cotización de hoy un mes de ventas sería inventar) o porque sólo hay datos por línea (overrides
    // sin perfil del cliente). En los dos casos, cada línea con sus números, en su moneda; la nota de
    // monedas sólo cuando ése es el motivo.
    const renderLinesList = (mixed) => (
        <div className="eco-out -lines">
            {mixed && <p className="eco-out__note">{t('client_panel.economy.unavailable.MIXED_CURRENCY')}</p>}
            <ul className="eco-lines" data-testid="eco-mixed-lines">
                {(data.lines || []).map((line) => {
                    const derived = line.derived || null;
                    const lineReason = derived ? null : economyReasonText(lineDerivedReason(line), t,
                        lineCurrencies(line, currency));
                    return (
                        <li className="eco-lines__row" key={line.business_unit_id}>
                            <span className="eco-lines__name">
                                {line.name}
                                {line.override === true && (
                                    <span className="eco-chip">{t('client_panel.economy.line.override')}</span>
                                )}
                                {line.has_store === false && (
                                    <span className="eco-chip -muted">{t('client_panel.economy.line.no_store')}</span>
                                )}
                            </span>
                            {derived ? (
                                <span className="eco-lines__nums">
                                    <strong>{formatRoas(derived.breakeven_roas) || '—'}</strong>
                                    <span>{formatSignedMoney(line.currency, derived.estimated_profit) || '—'}</span>
                                </span>
                            ) : (
                                <span className="eco-lines__reason">{lineReason || '—'}</span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );

    const renderActuals = () => {
        const actuals = data.actuals;
        if (!actuals) return null;
        return (
            <div className="eco-actuals" data-testid="eco-actuals">
                <span className="eco-actuals__label">{t('client_panel.economy.actuals.title')}</span>
                <strong className="eco-actuals__value">
                    {formatSignedMoney(actuals.currency || currency, actuals.profit) || '—'}
                </strong>
                <span className="eco-actuals__hint">
                    {t('client_panel.economy.actuals.hint', {
                        date: formatEconomyDate(actuals.as_of, language) || '—',
                        interpolation: {escapeValue: false},
                    })}
                </span>
            </div>
        );
    };

    const renderDrift = () => {
        const pct = Number(data.ticket_drift_pct);
        const loaded = Number(profile.avg_ticket);
        if (data.ticket_drift_pct === null || data.ticket_drift_pct === undefined) return null;
        if (!Number.isFinite(pct) || Math.abs(pct) <= TICKET_DRIFT_THRESHOLD_PCT) return null;
        if (!Number.isFinite(loaded) || loaded <= 0) return null;
        return (
            <p className="eco-note -warn" data-testid="eco-drift">
                {t('client_panel.economy.drift.hint', {
                    real: formatFullMoney(currency, loaded * (1 + pct / 100)),
                    pct: formatGoalValue(Math.abs(pct)),
                    loaded: formatFullMoney(currency, loaded),
                    interpolation: {escapeValue: false},
                })}
            </p>
        );
    };

    // «Usar los datos de Escalafy»: una versión ESCALAFY nueva, vigente hoy. Es la única escritura
    // de la card (la lectura sigue siendo el bloque del agregado): al terminar, el Panel se refresca.
    const applyEscalafy = () => {
        const connectionId = data.escalafy && data.escalafy.connection_id;
        if (!connectionId || usingEscalafy) return;
        setUsingEscalafy(true);
        ClientEconomicsApi.escalafyUse(connectionId).then((response) => {
            setUsingEscalafy(false);
            if (!response || response.status !== 'OK') {
                // Los dos casos previsibles dicen qué pasa; el resto, el genérico (nunca el texto del BE).
                const code = response && (response.error_code || response.status);
                toast.error(t(ESCALAFY_USE_ERROR_KEYS[code] || 'economics.error.generic'));
                return;
            }
            toast.success(t('economics.form.saved', {version: (response.data && response.data.version) || '—'}));
            if (onRefetch) onRefetch();
        }).catch(() => {
            setUsingEscalafy(false);
            toast.error(t('economics.error.generic'));
        });
    };

    /**
     * Cuando una corrección manual posterior le ganó a lo importado, el sync no la pisa y deja una
     * propuesta (`escalafy.proposal`): se dice qué propone Escalafy contra lo vigente, con la opción
     * de volver a lo importado.
     */
    const renderProposal = () => {
        const proposal = data.escalafy && data.escalafy.proposal;
        const proposed = (proposal && proposal.values) || {};
        const fields = PROFILE_FIELDS.filter((field) => proposed[field] !== null && proposed[field] !== undefined
            && Number(proposed[field]) !== Number(profile[field]));
        if (!fields.length) return null;
        // Los avisos de la derivación van al lado del diff: sin ellos se puede apretar «Usar» sin saber
        // que el margen mezcla MercadoLibre o que los honorarios se adivinaron (K-ecofe-06).
        const warnings = ((proposal && proposal.warnings) || []).filter((code) => ESCALAFY_WARNINGS.has(code));
        return (
            <div className="eco-proposal" data-testid="eco-escalafy-proposal">
                <ul>
                    {fields.map((field) => (
                        <li key={field}>
                            {t('client_panel.economy.escalafy.proposes', {
                                field: t(`client_panel.economy.input.${field}`),
                                value: formatProfileValue(field, proposed[field], currency) || '—',
                                current: formatProfileValue(field, profile[field], currency) || '—',
                                interpolation: {escapeValue: false},
                            })}
                        </li>
                    ))}
                    {warnings.map((code) => (
                        <li key={code} className="eco-proposal__warn" data-testid={`eco-escalafy-warning-${code}`}>
                            {t(`client_panel.economy.escalafy.warning.${code}`)}
                        </li>
                    ))}
                </ul>
                {data.escalafy.connection_id && (
                    <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm" onClick={applyEscalafy}
                            disabled={usingEscalafy} data-testid="eco-escalafy-use">
                        {t('client_panel.economy.escalafy.use')}
                    </button>
                )}
            </div>
        );
    };

    const renderEscalafyChip = () => {
        const escalafy = data.escalafy;
        if (!escalafy || escalafy.connected !== true) return null;
        const status = escalafy.reconnect_required ? 'DISCONNECTED' : (escalafy.status || 'OK');
        if (status === 'OK') {
            return (
                <span className="eco-src -ok" data-testid="eco-escalafy">
                    <span className="eco-src__dot"/>
                    {t('client_panel.economy.escalafy.connected', {
                        org: escalafy.org_name || '—',
                        when: relativeTimeLabel(escalafy.last_sync_at, t) || '—',
                        interpolation: {escapeValue: false},
                    })}
                </span>
            );
        }
        return (
            <Link className="eco-src -warn" data-testid="eco-escalafy" to={`${economiaPath}#escalafy`}>
                <span className="eco-src__dot"/>
                {t(ESCALAFY_STATUS_KEYS[status] || ESCALAFY_STATUS_KEYS.DISCONNECTED)}
            </Link>
        );
    };

    const renderFoot = () => {
        const sourceKey = SOURCE_LABEL_KEYS[profile.source];
        const source = sourceKey ? t(sourceKey) : (profile.source_label || profile.source || '—');
        const staleDays = daysSince(profile.effective_from);
        return (
            <div className="eco-foot">
                {/* Sin perfil del cliente (sólo overrides por línea) no hay una versión que citar. */}
                {data.profile && (
                    <span className="eco-src">
                        <span className="eco-src__dot"/>
                        {t('client_panel.economy.subtitle_source', {
                            source,
                            version: profile.version || '—',
                            date: formatEconomyDate(profile.effective_from, language) || '—',
                            interpolation: {escapeValue: false},
                        })}
                    </span>
                )}
                {renderEscalafyChip()}
                {data.stale === true ? (
                    <span className="eco-src -warn">
                        <span className="eco-src__dot"/>
                        {t('client_panel.economy.stale.hint', {days: staleDays === null ? '—' : staleDays})}
                    </span>
                ) : (data.stale_in_days !== null && data.stale_in_days !== undefined && (
                    <span className="eco-src -soft">
                        {t('client_panel.economy.stale.in_days', {
                            days: data.stale_in_days, count: Number(data.stale_in_days),
                        })}
                    </span>
                ))}
                <span className="pnl-card__spacer"/>
                <Link className="pnl-card__link" to={`${economiaPath}#versiones`}>
                    {t('client_panel.economy.versions')}
                </Link>
            </div>
        );
    };

    const renderLoaded = () => {
        // «Monedas distintas» sólo si el backend lo dice: `client_totals: null` también sale sin perfil
        // del cliente (sólo overrides por línea), y ahí no hay monedas mezcladas (R-ecofe-18).
        const mixed = reasonFor(data, 'client_totals') === 'MIXED_CURRENCY';
        const hasProfile = !!data.profile;
        return (
            <>
                <div className={`eco-body${hasProfile ? '' : ' -single'}`}>
                    {hasProfile && renderInputs()}
                    {data.client_totals ? renderTiles(data.client_totals) : renderLinesList(mixed)}
                </div>
                {data.client_totals && renderLinesWithoutStore(data.client_totals)}
                {data.client_totals && renderExcludedLines(data.client_totals)}
                {renderActuals()}
                {renderProposal()}
                {renderDrift()}
                {renderFoot()}
            </>
        );
    };

    // El bloque falló en el backend (`error: 'ECONOMY_UNAVAILABLE'`): un error NO se pinta como «sin
    // cargar». Si no, invita a cargar de nuevo un perfil que existe, y una carga con la misma fecha
    // reemplaza la versión del día (R-ecofe-02).
    const renderError = () => (
        <div className="eco-error" data-testid="eco-card-error">
            <p className="eco-error__txt">{t('economics.page.load_error')}</p>
            {onRefetch && (
                <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--sm" onClick={onRefetch}>
                    {t('client_panel.error_retry')}
                </button>
            )}
        </div>
    );

    const renderContent = () => {
        if (data.error) return renderError();
        return configured ? renderLoaded() : renderEmpty();
    };

    return (
        <div className="box pnl-card eco-card" data-testid="eco-card">
            {renderHead()}
            <div className="pnl-cnt">
                {renderContent()}
            </div>
        </div>
    );
}

export default EconomyCard;
